import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";
import Logo from "@/branding/Logo";
import { useAuth } from "@/auth/AuthProvider";

export default function Navbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const NAV_ITEMS = [
    { to: "/", label: t("nav.home"), id: "home" },
    { to: "/about", label: t("nav.about"), id: "about" },
    { to: "/products/jalna-sweet-lime-mosambi", label: t("nav.sweet_lime"), id: "mosambi" },
    { to: "/farmer-network", label: t("nav.farmer_network"), id: "farmer" },
    { to: "/fpo-partnership", label: t("nav.fpo_partnership"), id: "fpo" },
    { to: "/buyers", label: t("nav.buyers"), id: "buyers" },
    { to: "/knowledge", label: t("knowledge.eyebrow"), id: "knowledge" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 tr-glass-header ${scrolled ? "shadow-[0_10px_30px_-20px_rgba(6,78,59,0.25)]" : ""}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo-link" className="tr-nolink flex-shrink-0 whitespace-nowrap">
          <Logo variant="light" size="xl" />
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5 flex-shrink" data-testid="desktop-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.id}
              to={item.to}
              data-testid={`nav-link-${item.id}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? "bg-[var(--tr-deep-green)] text-white"
                    : "text-[var(--tr-deep-green)] hover:bg-[var(--tr-sage)]"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
          <LanguageSwitcher variant="compact" testid="nav-lang" />
          {user ? (
            <>
              {["administrator","super_administrator"].includes(user.role) && (
                <Link to="/admin" data-testid="nav-admin-btn" className="tr-pill-btn tr-btn-outline text-sm !px-4 !py-2 !min-h-0">Admin</Link>
              )}
              <button data-testid="nav-logout-btn" onClick={() => { logout(); navigate("/"); }} className="tr-pill-btn tr-btn-outline text-sm !px-4 !py-2 !min-h-0">{t("auth.logout")}</button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login-btn" className="tr-pill-btn tr-btn-outline text-sm !px-4 !py-2 !min-h-0">{t("auth.login")}</Link>
          )}
          <Link to="/buyers" data-testid="nav-order-btn" className="tr-pill-btn tr-btn-zest text-sm !px-4 !py-2 !min-h-0">
            {t("nav.enquire_bulk_order")}
          </Link>
        </div>

        <div className="xl:hidden flex items-center gap-2">
          <LanguageSwitcher variant="compact" testid="mobile-lang" />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[rgba(6,78,59,0.15)] bg-white"
            aria-label={t("nav.open_menu")}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-[rgba(6,78,59,0.08)]" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-2">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.id}
                to={item.to}
                data-testid={`mobile-nav-${item.id}`}
                className={({ isActive }) =>
                  `px-4 py-4 rounded-xl text-base font-semibold ${
                    isActive
                      ? "bg-[var(--tr-deep-green)] text-white"
                      : "text-[var(--tr-deep-green)] hover:bg-[var(--tr-sage)]"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="mobile-nav-contact" className="tr-pill-btn tr-btn-outline mt-2">
              {t("nav.contact")}
            </Link>
            <Link to="/buyers" data-testid="mobile-nav-order" className="tr-pill-btn tr-btn-zest">
              {t("nav.enquire_bulk_order")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
